// src/components/Auth/ResetPassword.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import mudoLogo from "../../assets/mudo-logo-bg.png";

const MIN_PASS_LEN = 8;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | updating | success

  useEffect(() => {
    let active = true;

    async function completeRecoveryFromUrl() {
      try {
        // Supabase v2 sends recovery links with tokens in the hash fragment.
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        const searchParams = new URLSearchParams(window.location.search || "");

        const type = hashParams.get("type") || searchParams.get("type");
        const accessToken = hashParams.get("access_token") || searchParams.get("access_token");
        const code = searchParams.get("code"); // fallback for code-based flows
        const token = accessToken || code;

        if (!token || type !== "recovery") {
          setError("Invalid or expired reset link.");
          return;
        }

        const { data, error } = await supabase.auth.exchangeCodeForSession(token);
        if (!active) return;
        if (error) {
          console.error("[ResetPassword] exchangeCodeForSession error", error);
          setError(error.message || "Could not restore password reset session.");
        } else {
          console.log("[ResetPassword] recovery session restored", data?.session?.user?.id);
        }
      } catch (err) {
        if (!active) return;
        console.error("[ResetPassword] unexpected error", err);
        setError("Something went wrong while restoring your reset session.");
      } finally {
        if (active) setLoading(false);
      }
    }

    completeRecoveryFromUrl();

    return () => {
      active = false;
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!newPassword || newPassword.length < MIN_PASS_LEN) {
      setError(`Password must be at least ${MIN_PASS_LEN} characters.`);
      return;
    }
    if (newPassword !== confirm) {
      setError("Passwords do not match.");
      return;
    }

    setStatus("updating");
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        console.error("[ResetPassword] updateUser error", updateError);
        setError(updateError.message || "Could not update password.");
        setStatus("idle");
        return;
      }

      setStatus("success");

      setTimeout(() => {
        navigate("/auth", { replace: true });
      }, 1500);
    } catch (err) {
      console.error("[ResetPassword] unexpected update error", err);
      setError("Something went wrong while updating your password.");
      setStatus("idle");
    }
  }

  if (loading) {
    return (
      <div className="w-screen h-screen bg-black text-white flex flex-col items-center justify-center">
        <img src={mudoLogo} alt="Mudo Logo" className="w-40 mb-6 opacity-90" />
        <div className="text-white/60 text-sm">Preparing password reset...</div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen bg-black text-white flex flex-col items-center justify-center px-6">
      <img
        src={mudoLogo}
        alt="Mudo Logo"
        className="w-40 mb-6 opacity-90"
        style={{
          filter:
            "drop-shadow(0 0 2px rgba(255,170,0,0.65)) drop-shadow(0 0 3px rgba(80, 80, 80, 0.32))",
        }}
      />

      <div className="bg-white/5 backdrop-blur-lg border border-white/20 p-6 rounded-xl w-full max-w-sm shadow-xl">
        {status === "success" ? (
          <div className="text-center">
            <div className="text-green-300 text-4xl mb-3">✓</div>
            <div className="text-white/90 font-semibold">Password updated!</div>
            <div className="text-white/50 text-sm mt-2">
              Redirecting you to the login screen…
            </div>
          </div>
        ) : (
          <>
            <h2 className="text-lg mb-4 text-center font-semibold text-white/90">
              Set a new password
            </h2>

            {error && (
              <div className="text-red-400 text-xs mb-3 text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <input
                type="password"
                placeholder={`New password (min ${MIN_PASS_LEN} characters)`}
                className="p-3 rounded-md bg-black/30 border border-white/20 text-white text-sm outline-none focus:ring-2 focus:ring-orange-400/40"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />

              <input
                type="password"
                placeholder="Confirm new password"
                className="p-3 rounded-md bg-black/30 border border-white/20 text-white text-sm outline-none focus:ring-2 focus:ring-orange-400/40"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />

              <button
                type="submit"
                disabled={status === "updating"}
                className="mt-2 bg-gradient-to-r from-yellow-800 to-orange-900 hover:from-yellow-900 hover:to-orange-900 py-3 rounded-md text-sm font-medium text-white shadow-[inset_0_0_1px_rgba(255,170,0,0.2)] disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  filter:
                    "drop-shadow(0 0 4px rgba(255,170,0,0.4)) drop-shadow(0 0 8px rgba(255,170,0,0.25))",
                }}
              >
                {status === "updating" ? "Updating…" : "Update password"}
              </button>
            </form>

            <div className="text-center text-[11px] text-white/40 mt-3">
              After updating, you&apos;ll use this password with your email to log into Mudo.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
